import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSnapshotContext } from '_/contexts/SnapshotContext';
import { useSourceContext } from '_/contexts/SourceContext';
import zeusSdk from '_/utils/SDKs';
import { Collapse, Typography, Alert } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';

import styles from './SnapshotCompare.module.css';

const { Text } = Typography;

interface Measure {
  avg?: number;
  stddev?: number;
  value?: number;
  absSwing?: number;
  name?: string;
  score?: number;
}

interface TopNMeasure extends Measure {
  name: string;
}

interface Metric {
  metricName: string;
  type: string;
  score: number;
  measureNames?: string[];
  measures: {
    [key: string]: Measure | TopNMeasure[];
  };
}

interface TargetGroup {
  target: string;
  score: number;
  metrics: Metric[];
}

interface NamespaceGroup {
  namespace: string;
  metrics: TargetGroup[];
}

const percentColors = [
  { pct: 0.5, color: { r: 0xff, g: 0x00, b: 0 } },
  { pct: 0.75, color: { r: 0xff, g: 0xff, b: 0 } },
  { pct: 1.0, color: { r: 0x00, g: 0xff, b: 0 } },
];

const getColorForPercentage = (pct: number): string => {
  if (pct == null) return '';
  let i = 1;
  for (; i < percentColors.length - 1; i++) {
    if (pct < percentColors[i].pct) {
      break;
    }
  }
  const lower = percentColors[i - 1];
  const upper = percentColors[i];
  const range = upper.pct - lower.pct;
  const rangePct = Math.min((pct - lower.pct) / range, 1);
  const pctLower = 1 - rangePct;
  const pctUpper = rangePct;
  const color = {
    r: Math.floor(lower.color.r * pctLower + upper.color.r * pctUpper),
    g: Math.floor(lower.color.g * pctLower + upper.color.g * pctUpper),
    b: Math.floor(lower.color.b * pctLower + upper.color.b * pctUpper),
  };
  return `rgb(${color.r},${color.g},${color.b})`;
};

const getScoreColor = (score?: number): string => {
  if (score == null) return '';
  return getColorForPercentage(score);
};

const getAbsSwingColor = (absSwing?: number): string => {
  if (absSwing == null) return '';
  return getColorForPercentage(1 - absSwing);
};

const formatThreeDp = (x: any): string => {
  if (x == null || x === '') return '(none)';
  if (Number.isInteger(x)) return String(x);
  return Number.parseFloat(x).toFixed(3);
};

const formatPct = (x: any): string => {
  if (x == null || x === '') return '';
  return (x * 100).toFixed(1);
};

function BooleanMetric({ metric }: { metric: Metric }) {
  const measure = metric.measures.pct_true as Measure;
  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Type</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th rowSpan={3} style={{ backgroundColor: getScoreColor(metric.score) }}>
            {metric.metricName}
          </th>
          <th rowSpan={3}>{formatThreeDp(metric.score)}</th>
          <td>Expected</td>
          <td>
            {formatPct(formatThreeDp(measure?.avg))} ±{formatPct(formatThreeDp((measure?.stddev || 0) * 2))}
          </td>
        </tr>
        <tr>
          <td>Actual</td>
          <td style={{ backgroundColor: getAbsSwingColor(measure?.absSwing) }} data-absswing={measure?.absSwing}>
            {formatPct(formatThreeDp(measure?.value))}
          </td>
        </tr>
        <tr>
          <td>Swing</td>
          <td>{formatPct(formatThreeDp(measure?.absSwing))}</td>
        </tr>
      </tbody>
    </table>
  );
}

function DistinctMetric({ metric }: { metric: Metric }) {
  const measure = metric.measures.pct_distinct as Measure;
  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Type</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th rowSpan={3} style={{ backgroundColor: getScoreColor(metric.score) }}>
            {metric.metricName}
          </th>
          <th rowSpan={3}>{formatThreeDp(metric.score)}</th>
          <td>Expected</td>
          <td>
            {formatPct(formatThreeDp(measure?.avg))} ±{formatPct(formatThreeDp((measure?.stddev || 0) * 2))}
          </td>
        </tr>
        <tr>
          <td>Actual</td>
          <td style={{ backgroundColor: getAbsSwingColor(measure?.absSwing) }} data-absswing={measure?.absSwing}>
            {formatPct(formatThreeDp(measure?.value))}
          </td>
        </tr>
        <tr>
          <td>Swing</td>
          <td>{formatPct(formatThreeDp(measure?.absSwing))}</td>
        </tr>
      </tbody>
    </table>
  );
}

function TopNMetric({ metric }: { metric: Metric }) {
  const measures = metric.measures as any as { [key: string]: TopNMeasure };
  const sortedMeasures = Object.values(measures).sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Values</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th style={{ backgroundColor: getScoreColor(metric.score) }}>{metric.metricName}</th>
          <th>{formatThreeDp(metric.score)}</th>
          <td>
            <table className={styles.metricTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Swing</th>
                </tr>
              </thead>
              <tbody>
                {sortedMeasures.map((measure, idx) => (
                  <tr key={idx}>
                    <th style={{ backgroundColor: getScoreColor(measure.score) }}>{measure.name}</th>
                    <td>
                      {measure.avg != null
                        ? `${formatPct(formatThreeDp(measure.avg))} ±${formatPct(formatThreeDp((measure.stddev || 0) * 2))}`
                        : ''}
                    </td>
                    <td style={{ backgroundColor: getAbsSwingColor(measure.absSwing) }} data-absswing={measure.absSwing}>
                      {formatPct(formatThreeDp(measure.value))}
                    </td>
                    <td>{formatPct(formatThreeDp(measure.absSwing))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function NotNullMetric({ metric }: { metric: Metric }) {
  const measure = metric.measures.pct_not_null as Measure;
  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Type</th>
          <th>% True</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th rowSpan={3} style={{ backgroundColor: getScoreColor(metric.score) }}>
            {metric.metricName}
          </th>
          <th rowSpan={3}>{formatThreeDp(metric.score)}</th>
          <td>Expected</td>
          <td>
            {formatPct(formatThreeDp(measure?.avg))} ±{formatPct(formatThreeDp((measure?.stddev || 0) * 2))}
          </td>
        </tr>
        <tr>
          <td>Actual</td>
          <td style={{ backgroundColor: getAbsSwingColor(measure?.absSwing) }} data-absswing={measure?.absSwing}>
            {formatPct(formatThreeDp(measure?.value))}
          </td>
        </tr>
        <tr>
          <td>Swing</td>
          <td>{formatPct(formatThreeDp(measure?.absSwing))}</td>
        </tr>
      </tbody>
    </table>
  );
}

function CountMetric({ metric }: { metric: Metric }) {
  const measure = metric.measures.count as Measure;
  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Type</th>
          <th>Count</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th rowSpan={3} style={{ backgroundColor: getScoreColor(metric.score) }}>
            {metric.metricName}
          </th>
          <th rowSpan={3}>{formatThreeDp(metric.score)}</th>
          <td>Expected</td>
          <td>
            {formatThreeDp(measure?.avg)} ±{formatThreeDp((measure?.stddev || 0) * 2)}
          </td>
        </tr>
        <tr>
          <td>Actual</td>
          <td style={{ backgroundColor: getAbsSwingColor(measure?.absSwing) }} data-absswing={measure?.absSwing}>
            {measure?.value}
          </td>
        </tr>
        <tr>
          <td>Swing</td>
          <td>{formatPct(formatThreeDp(measure?.absSwing))}</td>
        </tr>
      </tbody>
    </table>
  );
}

function NumericMetric({ metric }: { metric: Metric }) {
  const fields = ['min', 'p01', 'p05', 'p25', 'p50', 'p75', 'p95', 'p99', 'max', 'avg'];

  return (
    <table className={styles.metricTable}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Score</th>
          <th>Type</th>
          <th>Min</th>
          <th>P01</th>
          <th>P05</th>
          <th>P25</th>
          <th>P50</th>
          <th>P75</th>
          <th>P95</th>
          <th>P99</th>
          <th>Max</th>
          <th>Avg</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th rowSpan={3} style={{ backgroundColor: getScoreColor(metric.score) }}>
            {metric.metricName}
          </th>
          <th rowSpan={3}>{formatThreeDp(metric.score)}</th>
          <td>Expected</td>
          {fields.map((field) => {
            const measure = metric.measures[field] as Measure;
            return (
              <td key={field}>
                {formatThreeDp(measure?.avg)} ±{formatThreeDp((measure?.stddev || 0) * 2)}
              </td>
            );
          })}
        </tr>
        <tr>
          <td>Actual</td>
          {fields.map((field) => {
            const measure = metric.measures[field] as Measure;
            return (
              <td key={field} style={{ backgroundColor: getAbsSwingColor(measure?.absSwing) }} data-absswing={measure?.absSwing}>
                {formatThreeDp(measure?.value)}
              </td>
            );
          })}
        </tr>
        <tr>
          <td>Swing</td>
          {fields.map((field) => {
            const measure = metric.measures[field] as Measure;
            return <td key={field}>{formatPct(formatThreeDp(measure?.absSwing))}</td>;
          })}
        </tr>
      </tbody>
    </table>
  );
}

function MetricRenderer({ metric }: { metric: Metric }) {
  switch (metric.type) {
    case 'boolean':
      return <BooleanMetric metric={metric} />;
    case 'distinct':
      return <DistinctMetric metric={metric} />;
    case 'topN':
      return <TopNMetric metric={metric} />;
    case 'not_null':
      return <NotNullMetric metric={metric} />;
    case 'count':
      return <CountMetric metric={metric} />;
    case 'numeric':
      return <NumericMetric metric={metric} />;
    default:
      return null;
  }
}

function SnapshotCompare(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProjectSlug, selectedProject } = useProject();
  const { selectedCollectionSlug, selectedCollection } = useCollection();
  const { selectedSourceSlug, selectedSource } = useSourceContext();
  const { snapshotId, isLoading: isLoadingSnapshot } = useSnapshotContext();
  const { setBreadcrumbs } = useTopHeader();

  const [metricsCompare, setMetricsCompare] = useState<NamespaceGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
      {
        title: selectedCollection?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      },
      {
        title: selectedSource?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
      },
      {
        title: 'Snapshots',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots`,
      },
      { title: 'Compare' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    selectedOrganization?.name,
    selectedOrgSlug,
    selectedProject?.name,
    selectedProjectSlug,
    selectedCollection?.name,
    selectedCollectionSlug,
    selectedSource?.name,
    selectedSourceSlug,
    snapshotId,
    setBreadcrumbs,
  ]);

  useEffect(() => {
    async function fetchMetricsCompare() {
      if (!snapshotId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await zeusSdk.snapshots.getMetricsCompare(snapshotId);

        if (!data) {
          throw new Error('No metrics compare data available');
        }

        // Data is already grouped by namespace → target → metrics; sort both levels
        const namespacesArray: NamespaceGroup[] = (Array.isArray(data) ? [...data] : [])
          .sort((a: NamespaceGroup, b: NamespaceGroup) => a.namespace.localeCompare(b.namespace))
          .map((ns: NamespaceGroup) => ({
            ...ns,
            metrics: [...ns.metrics].sort((a, b) => a.target.localeCompare(b.target)),
          }));

        setMetricsCompare(namespacesArray);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching metrics compare:', err);
        setError(err.message || 'Failed to fetch metrics compare data');
        setMetricsCompare([]);
      } finally {
        setIsLoading(false);
      }
    }

    void fetchMetricsCompare();
  }, [snapshotId]);

  if (isLoadingSnapshot || isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Alert title="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  if (metricsCompare.length === 0) {
    return (
      <div className={styles.container}>
        <Alert title="No Data" description="No metrics comparison data available for this snapshot." type="info" showIcon />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {metricsCompare.map((namespaceGroup) => {
        const items = namespaceGroup.metrics.map((targetGroup) => {
          const sortedMetrics = [...targetGroup.metrics].sort((a, b) => a.metricName.localeCompare(b.metricName));

          return {
            key: `${namespaceGroup.namespace}-${targetGroup.target}`,
            label: (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{targetGroup.target}</span>
                <Text type="secondary" style={{ fontSize: '0.85em', marginLeft: '8px' }}>
                  Score: {formatThreeDp(targetGroup.score)}
                </Text>
              </div>
            ),
            style: { backgroundColor: getScoreColor(targetGroup.score) },
            children: (
              <div className={styles.metricsContainer}>
                {sortedMetrics.map((metric, idx) => (
                  <div key={idx} className={styles.metricWrapper}>
                    <MetricRenderer metric={metric} />
                  </div>
                ))}
              </div>
            ),
          };
        });

        return (
          <div key={namespaceGroup.namespace} className={styles.namespaceGroup}>
            <h2 className={styles.namespaceTitle}>{namespaceGroup.namespace}</h2>
            <Collapse items={items} />
          </div>
        );
      })}
    </div>
  );
}

export default SnapshotCompare;
